import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";

import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { PricingProvider } from "@/lib/pricingContext";
import { useDocumentHead } from "@/seo/useDocumentHead";
import { FAQ } from "@/routes/FAQ";
import { HelpCenter } from "@/routes/HelpCenter";
import { HIPAANotice } from "@/routes/HIPAANotice";
import { HowItWorks } from "@/routes/HowItWorks";
import { Landing } from "@/routes/Landing";
import { NotFound } from "@/routes/NotFound";
import { Pricing } from "@/routes/Pricing";
import { PrivacyPolicy } from "@/routes/PrivacyPolicy";
import { Security } from "@/routes/Security";
import { TermsOfService } from "@/routes/TermsOfService";

// Marketing routes are imported EAGERLY and must stay that way.
//
// These routes are prerendered to static HTML at build time and hydrated
// in the browser. `React.lazy` is incompatible with that: the client's
// first render would be the Suspense fallback (an empty div) while the
// server HTML holds the full page, which is a hydration mismatch on every
// prerendered page. Lazy-loading a route you hydrate is also self-
// defeating — its code is needed on first paint regardless, so the split
// only buys an extra round-trip before the page becomes interactive.
//
// App routes below stay code-split. They are never prerendered, they are
// the heavy ones (markdown, framer-motion, diff), and no crawler or
// first-paint budget depends on them. Manual vendor chunking lives in
// `vite.config.ts`.
const CheckoutCancel = lazy(() =>
  import("@/routes/CheckoutCancel").then((m) => ({ default: m.CheckoutCancel })),
);
const CheckoutSuccess = lazy(() =>
  import("@/routes/CheckoutSuccess").then((m) => ({ default: m.CheckoutSuccess })),
);
const DeliveryRecover = lazy(() =>
  import("@/routes/DeliveryRecover").then((m) => ({ default: m.DeliveryRecover })),
);
const ReportOutcome = lazy(() =>
  import("@/routes/ReportOutcome").then((m) => ({ default: m.ReportOutcome })),
);
const RestoreSession = lazy(() =>
  import("@/routes/RestoreSession").then((m) => ({ default: m.RestoreSession })),
);
const Session = lazy(() =>
  import("@/routes/Session").then((m) => ({ default: m.Session })),
);
const Unsubscribe = lazy(() =>
  import("@/routes/Unsubscribe").then((m) => ({ default: m.Unsubscribe })),
);

function RouteFallback() {
  // Lightweight, theme-matching fallback. Avoids a spinner on fast
  // navigations (chunks usually resolve in <1 frame from cache).
  return <div className="min-h-screen bg-bg" aria-busy="true" />;
}

export function App() {
  // Prerendering fixes the head only for the document a visitor lands on;
  // this keeps title/description/canonical correct across client-side
  // navigation. No-op during prerender, since effects don't run there.
  useDocumentHead();

  // Top-level boundary: any uncaught render error inside a route now
  // surfaces a visible "Something broke" card instead of a blank page.
  // Without this, a throw anywhere in the tree unmounts the whole app
  // and the user sees nothing — which is exactly the failure mode that
  // hid the real bug behind the post-export feedback star click.
  return (
    <ErrorBoundary scope="App">
      <PricingProvider>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/session" element={<Session />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/hipaa" element={<HIPAANotice />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/security" element={<Security />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/checkout/cancel" element={<CheckoutCancel />} />
            <Route path="/delivery/recover" element={<DeliveryRecover />} />
            <Route path="/restore" element={<RestoreSession />} />
            <Route path="/report-outcome/:token" element={<ReportOutcome />} />
            <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </PricingProvider>
    </ErrorBoundary>
  );
}
